/* eslint-disable @typescript-eslint/no-explicit-any */
import { DayType } from 'components/CruiseDatesFilter/DatesCalendar/DatesCalendar.types';
import { ADULTS_FILTER_OPTION, CHILDREN_FILTER_OPTION } from 'components/CruiseSearchWidget/config';
import {
	DEFAULT_FILTER_KEY_TO_CUSTOM_FILTER_CONFIG,
	EVENT_CLEAN_WIDGET_FILTERS,
	NUMBER_OF_GUESTS_FILTER_KEY,
	PROMO_KEY,
} from 'components/CruiseSearchWidget/constants';
import { CalendarType, CustomFilterConfig, FilterDropdownConfig } from 'components/CruiseSearchWidget/types';
import { isUniqueFilter } from 'components/CruiseSearchWidget/utils/reducers';
import { ADULTS, CHILDREN } from 'components/NumberOfGuests/constants';
import { AppProvider } from 'contexts/appContext';
import { CUSTOM_FILTERS_KEY, FilterFacet, getFiltersFacets } from 'lib/apiClient';
import { BrandCode, Locale } from 'lib/config/config';
import { UpdateSelectionItem, mapFilters, updateAllFiltersState, updateFilterState, updateFiltersFacets } from 'lib/mappers';
import { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FacetingRequest, FeatureFlags, FilterOption, Filters, PromoSelection, Promotion, SelectedOption, Selection } from './types';
import { useFiltersApi } from './useFiltersApi';
import { useLocale } from './useLocale';
import { getSelectionUpdatedByPromotion, updateFiltersFacetsWithAppliedPromotion } from './utils/faceting';
import { updateFiltersSelection } from './utils/selectionHandler/filtersSelection';
import { getNewHistoricalSelection, initialSelectionToHistoricalSelection } from './utils/selectionHandler/historicalSelection';

export interface FiltersProviderProps {
	children: ReactNode;
	initialValue?: Array<unknown>;
	initialSelection?: Selection;
	externalSelection?: Selection;
	initialPromoSelection?: PromoSelection;
	onUpdateSelection?: (selection: Selection, updateAppliedSelection?: boolean) => void;
	onAppliedSelection?: (isUserInteraction: boolean) => void;
	onResetSelection?: () => void;
	promotionData: Promotion;
	locale: Locale;
	brandCode: BrandCode;
	featureFlags: FeatureFlags;
}

export interface FiltersContextState {
	filters: Filters;
	isLoading: boolean;
	selection: Selection;
	locale: Locale;
	updateSelection: (filter: string, selectedValue: UpdateSelectionItem, updateAppliedSelection?: boolean) => Promise<void>;
	applied: Selection;
	updateApplied: (passedSelection: Selection | null, passedHistoricalSelection: Array<SelectedOption> | null, isUserInteraction: boolean) => void;
	activeFilter: FilterDropdownConfig | null;
	setActiveFilter: Dispatch<SetStateAction<FilterDropdownConfig | null>>;
	enabledFilters: Array<FilterDropdownConfig>;
	setEnabledFilters: Dispatch<SetStateAction<Array<FilterDropdownConfig>>>;
	clearSelection: (updateAppliedSelection?: boolean) => void;
	clearApplied: () => void;
	historicalSelection: Array<SelectedOption>;
	appliedHistoricalSelection: Array<SelectedOption>;
	removeHistoricalSelectionPill: (filter: SelectedOption, updateAppliedSelection?: boolean) => void;
	datesRangeSel: Array<DayType>;
	setDatesRangeSel: Dispatch<SetStateAction<Array<DayType>>>;
	resetSelectionToApplied: () => Promise<void>;
	enableUniqueFilters: boolean;
	setEnableUniqueFilters: Dispatch<SetStateAction<boolean>>;
	filterKeyToCustomFilterConfig: Record<string, CustomFilterConfig>;
	setFilterKeyToCustomFilterConfig: Dispatch<Record<string, CustomFilterConfig>>;
	featureFlags: FeatureFlags;
	promotionData: Promotion;
}

export const DEFAULT_SELECTION: Selection = {
	accessible: [],
	adults: [],
	cabinClass: [],
	children: [],
	custom: [],
	departurePort: [],
	destination: [],
	nights: [],
	port: [],
	price: [],
	startDate: [],
	ship: [],
	promo: [],
	voyageType: [],
};

const FiltersContext = createContext<FiltersContextState>(undefined as unknown as FiltersContextState);

export const useFiltersContext = () => useContext(FiltersContext);

export function FiltersProvider({
	children,
	initialValue,
	onUpdateSelection,
	onAppliedSelection,
	initialSelection,
	onResetSelection,
	externalSelection,
	initialPromoSelection,
	promotionData,
	locale: externalLocale,
	brandCode,
	featureFlags,
}: Readonly<FiltersProviderProps>) {
	const { data: locale } = useLocale(externalLocale, brandCode);
	const { data, isLoading } = useFiltersApi(locale, brandCode, initialValue);
	const filterData = mapFilters(data as Filters, locale);
	const [filters, setFilters] = useState<Filters>(updateFiltersFacets(filterData, data as FilterFacet[]));
	const [selection, setSelection] = useState<Selection>({ ...DEFAULT_SELECTION, ...initialSelection });
	const [applied, setApplied] = useState({ ...DEFAULT_SELECTION, ...initialSelection });
	const [activeFilter, setActiveFilter] = useState<FilterDropdownConfig | null>(null);
	const [enabledFilters, setEnabledFilters] = useState<FilterDropdownConfig[]>([]);
	const [historicalSelection, setHistoricalSelection] = useState<Array<SelectedOption>>([]);
	const [appliedHistoricalSelection, setAppliedHistoricalSelection] = useState<Array<SelectedOption>>([]);
	const [datesRangeSel, setDatesRangeSel] = useState<Array<DayType>>([]);
	const [enableUniqueFilters, setEnableUniqueFilters] = useState<boolean>(false);
	const facetingRequest = useRef<FacetingRequest | null>(null);
	const prevLanguage = useRef<string>(externalLocale.language);
	const [filterKeyToCustomFilterConfig, setFilterKeyToCustomFilterConfig] = useState<Record<string, CustomFilterConfig>>(
		DEFAULT_FILTER_KEY_TO_CUSTOM_FILTER_CONFIG,
	);
	const [isInitialPromoApplied, setIsInitialPromoApplied] = useState<boolean>(false);

	useEffect(() => {
		if (!isLoading && prevLanguage.current !== locale.language) {
			setFilters(filterData);
			prevLanguage.current = locale.language;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	document.addEventListener(EVENT_CLEAN_WIDGET_FILTERS, () => {
		clearApplied();
	});

	if (
		enabledFilters.some((filter) => filter.key === NUMBER_OF_GUESTS_FILTER_KEY) &&
		filters.length &&
		!filters.some((filter) => filter.key === ADULTS) &&
		!filters.some((filter) => filter.key === CHILDREN)
	) {
		setFilters([...filters, ADULTS_FILTER_OPTION, CHILDREN_FILTER_OPTION]);
	}

	useEffect(() => {
		if (locale?.office) setFiltersFacets(selection, filters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [promotionData?.code, locale]);

	const setFiltersFacets = useCallback(
		async (selection: Selection, filters: Filters) => {
			const abortController = new AbortController();
			let selectionForfaceting = { ...selection };

			try {
				if (facetingRequest.current !== null) {
					facetingRequest.current.abortController.abort('New faceting applied');
					facetingRequest.current = null;
				}

				if (selection[PROMO_KEY]?.length > 0 && promotionData && promotionData.valid && promotionData.criteriaToSearchSelection) {
					selectionForfaceting = getSelectionUpdatedByPromotion(selection, promotionData);
				}

				facetingRequest.current = {
					facets: getFiltersFacets(selectionForfaceting, abortController, locale, brandCode),
					abortController,
				};

				const facetResponse = await facetingRequest.current.facets;
				facetingRequest.current = null;

				const filtersFacets = updateFiltersFacetsWithAppliedPromotion({
					promotion: promotionData,
					filtersFacets: facetResponse,
					promoSelection: initialPromoSelection,
				});

				setFilters(updateFiltersFacets(filters, filtersFacets));
			} catch (error) {
				console.error('Error updating faceting: ', error);
			}
		},
		[setFilters, promotionData, locale, brandCode, initialPromoSelection],
	);

	const updateApplied = useCallback(
		(passedSelection: Selection | null, passedHistoricalSelection: Array<SelectedOption> | null, isUserInteraction = false) => {
			const newSelection = passedSelection ?? selection;
			setApplied(newSelection);
			const newAppliedHistoricalSelection = (passedHistoricalSelection ?? historicalSelection).filter(
				(option) => option.filterKey !== ADULTS && option.filterKey !== CHILDREN,
			);
			setAppliedHistoricalSelection(newAppliedHistoricalSelection);
			if (onAppliedSelection) {
				onAppliedSelection(isUserInteraction);
			}
		},
		[selection, setApplied, onAppliedSelection, setAppliedHistoricalSelection, historicalSelection],
	);

	const updateSelection = useCallback(
		async (filterKey: string, selectedValue: UpdateSelectionItem, updateAppliedSelection = false): Promise<void> => {
			const newSelection = updateFiltersSelection(filterKey, selectedValue, selection, historicalSelection);

			const customFilterIsSelected =
				isUniqueFilter(selectedValue) && newSelection[CUSTOM_FILTERS_KEY].filter((value) => selectedValue.value.includes(value)).length > 0;
			const updatedFilters: Filters = customFilterIsSelected
				? updateAllFiltersState(filters, newSelection)
				: updateFilterState(filters, filterKey, selectedValue);
			const filterSelected = updatedFilters.find((filter: FilterOption) => filter.key === filterKey) as FilterOption;
			const newHistoricalSelection = getNewHistoricalSelection(filterSelected, selectedValue, historicalSelection, locale);
			setSelection(newSelection);
			setFilters(updatedFilters);
			setHistoricalSelection(newHistoricalSelection);

			if (onUpdateSelection) {
				onUpdateSelection(newSelection, updateAppliedSelection);
			}

			if (updateAppliedSelection) {
				updateApplied(newSelection, newHistoricalSelection);
			}

			setFiltersFacets(newSelection, updatedFilters);
		},
		[selection, filters, historicalSelection, onUpdateSelection, updateApplied, setFiltersFacets, locale],
	);

	const clearSelection = useCallback(
		(updateAppliedSelection = false) => {
			setSelection(DEFAULT_SELECTION);
			setHistoricalSelection([]);
			setFilters(filterData);
			setDatesRangeSel([]);

			if (onUpdateSelection) {
				onUpdateSelection(DEFAULT_SELECTION, updateAppliedSelection);
			}
		},
		[setSelection, setFilters, filterData, onUpdateSelection],
	);

	const clearApplied = useCallback(() => {
		setApplied(DEFAULT_SELECTION);
		setAppliedHistoricalSelection([]);
		clearSelection(true);
	}, [setApplied, setAppliedHistoricalSelection, clearSelection]);

	const removeHistoricalSelectionPill = useCallback(
		(filter: SelectedOption, updateAppliedSelection = false) => {
			const selectedValue: UpdateSelectionItem = {
				value: filter.option.dateRanges ? filter.option.dateRanges : [filter.option.code],
				group: filter.group,
				year: filter.year,
				calendarType: filter.calendarType,
				containers: filter.containers,
			};

			if (filter.calendarType === CalendarType.Dates) {
				setDatesRangeSel([]);
			}
			updateSelection(filter.filterKey, selectedValue, updateAppliedSelection);
		},
		[updateSelection],
	);

	const resetSelectionToApplied = useCallback(async () => {
		if (applied === selection) {
			return;
		}

		if (!applied.startDate.length) {
			setDatesRangeSel([]);
		}
		setSelection(applied);
		setHistoricalSelection(appliedHistoricalSelection);
		setFiltersFacets(applied, updateAllFiltersState(filterData, applied));

		if (onResetSelection) {
			onResetSelection();
		}
	}, [applied, filterData, selection, appliedHistoricalSelection, setFiltersFacets, onResetSelection]);

	const updateWithNewSelection = useCallback(
		(newSelection: Selection) => {
			const updatedFilters = updateAllFiltersState(filterData, newSelection);
			const updatedHistoricalSelection = initialSelectionToHistoricalSelection(updatedFilters, newSelection, locale, promotionData);
			setFilters(updatedFilters);
			setHistoricalSelection(updatedHistoricalSelection);
			updateApplied(newSelection, updatedHistoricalSelection);
			setFiltersFacets(newSelection, updatedFilters);
		},
		[filterData, updateApplied, setFiltersFacets, setHistoricalSelection, setFilters, locale, promotionData],
	);

	const applyInitialSelection = useCallback(() => {
		const newSelection = {
			...DEFAULT_SELECTION,
			...(initialSelection ?? {}),
			...(initialPromoSelection ?? {}),
		};
		updateWithNewSelection(newSelection);
		setSelection(newSelection);
	}, [initialSelection, initialPromoSelection, updateWithNewSelection]);

	useEffect(() => {
		if (filterData.length && !isLoading && !filters.length && initialSelection !== null) {
			setFilters(filterData);
			applyInitialSelection();
		}
	}, [filterData, isLoading, filters, initialSelection, applyInitialSelection]);

	useEffect(() => {
		if (externalSelection) {
			updateWithNewSelection(externalSelection);
			setSelection({ ...DEFAULT_SELECTION, ...externalSelection });
		}
	}, [externalSelection, updateWithNewSelection]);

	useEffect(() => {
		if (initialPromoSelection && promotionData?.valid && !isInitialPromoApplied) {
			const newSelection = { ...selection, ...initialPromoSelection };
			updateWithNewSelection(newSelection);
			setSelection(newSelection);
			setIsInitialPromoApplied(true);
		}
	}, [initialPromoSelection, selection, promotionData, isInitialPromoApplied, updateWithNewSelection]);

	const value = useMemo<FiltersContextState>(
		() => ({
			locale,
			filters,
			selection,
			isLoading,
			updateSelection,
			activeFilter,
			setActiveFilter,
			enabledFilters,
			setEnabledFilters,
			clearSelection,
			historicalSelection,
			applied,
			updateApplied,
			removeHistoricalSelectionPill,
			appliedHistoricalSelection,
			clearApplied,
			datesRangeSel,
			setDatesRangeSel,
			resetSelectionToApplied,
			enableUniqueFilters,
			setEnableUniqueFilters,
			filterKeyToCustomFilterConfig,
			setFilterKeyToCustomFilterConfig,
			featureFlags,
			promotionData,
		}),
		[
			filters,
			isLoading,
			selection,
			activeFilter,
			enabledFilters,
			updateSelection,
			clearSelection,
			historicalSelection,
			applied,
			updateApplied,
			removeHistoricalSelectionPill,
			appliedHistoricalSelection,
			clearApplied,
			datesRangeSel,
			resetSelectionToApplied,
			enableUniqueFilters,
			filterKeyToCustomFilterConfig,
			setFilterKeyToCustomFilterConfig,
			locale,
			featureFlags,
			promotionData,
		],
	);

	if (!locale) return <></>;

	return (
		<FiltersContext.Provider value={value}>
			<AppProvider locale={locale} brandCode={brandCode}>
				{children}
			</AppProvider>
		</FiltersContext.Provider>
	);
}
